import { ArrowUpRight, Instagram, Mail } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const CHANNELS = [
  {
    label: "Email — Fastest Response",
    value: () => site.email,
    href: `mailto:${site.email}`,
    icon: Mail,
    accent: "purple",
    testId: "contact-email-link",
    external: false,
  },
  {
    label: "Instagram",
    value: () => site.igHandle,
    href: site.igUrl,
    icon: Instagram,
    accent: "gold",
    testId: "contact-instagram-link",
    external: true,
  },
  {
    label: "TikTok",
    value: () => site.tiktokHandle,
    href: site.tiktokUrl,
    icon: TikTokIcon,
    accent: "purple",
    testId: "contact-tiktok-link",
    external: true,
  },
];

export default function Contact() {
  return (
    <section
      id="contact"
      className="relative overflow-hidden border-t border-white/10 py-28 lg:py-40"
      data-testid="contact-section"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{
          backgroundImage: `url(${site.images.neon})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
        }}
      />
      <div className="halftone-gold pointer-events-none absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#08080A] via-transparent to-[#08080A]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal>
          <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold" />
            Contact
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-white sm:text-7xl lg:text-8xl">
            Ready to<br />
            <span className="text-purple drop-shadow-[0_0_25px_rgba(168,50,255,0.55)]">send it?</span>
          </h2>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-8 max-w-lg text-base font-medium leading-relaxed text-white/70 lg:text-lg">
            Tell me where you're racing, what the car demands, and where you
            want to be. I'll build the program that gets you there.
          </p>
        </Reveal>

        <div className="mt-16 flex flex-col gap-6">
          {CHANNELS.map((channel, i) => {
            const Icon = channel.icon;
            const accentText = channel.accent === "gold" ? "text-gold" : "text-purple";
            const accentHover = channel.accent === "gold" ? "hover:border-gold" : "hover:border-purple";
            const accentHoverText = channel.accent === "gold" ? "group-hover:text-gold" : "group-hover:text-purple";
            return (
              <Reveal key={channel.label} delay={0.25 + i * 0.1}>
                <a
                  href={channel.href}
                  {...(channel.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className={`group flex flex-wrap items-center justify-between gap-4 border-b-2 border-white/15 pb-6 transition-colors duration-200 ${accentHover}`}
                  data-testid={channel.testId}
                >
                  <span className="flex items-center gap-4">
                    <Icon className={`h-6 w-6 shrink-0 ${accentText}`} />
                    <span>
                      <span className="block text-[10px] font-black uppercase tracking-[0.3em] text-white/40">
                        {channel.label}
                      </span>
                      <span className={`break-all font-display text-base font-black uppercase tracking-tight text-white transition-colors duration-200 sm:text-3xl lg:text-5xl ${accentHoverText}`}>
                        {channel.value()}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className={`h-8 w-8 shrink-0 text-white/40 transition-all duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 ${accentHoverText}`} />
                </a>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.55}>
          <p className="mt-10 text-xs font-bold uppercase tracking-[0.25em] text-white/40">
            My fastest response time is by email — every message gets read.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
